import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X, Download, Check } from "lucide-react";
import { getUtmAttribution } from "@/utils/utm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISMISSED_KEY = "cb_exit_intent_dismissed";
const CONVERTED_KEY = "cb_exit_intent_converted";
const SHOW_DELAY_MS = 8000; // don't fire in the first 8s of a visit

// Only fire on high-intent public pages; skip admin, contact, privacy etc.
const ELIGIBLE_PATHS = [/^\/$/, /^\/services(\/|$)/, /^\/industries$/, /^\/case-studies(\/|$)/, /^\/insights(\/|$)/, /^\/about$/];

const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", email: "", company: "", website: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");
  const [armed, setArmed] = useState(false);

  const isEligiblePath = useCallback(() => {
    if (typeof window === "undefined") return false;
    return ELIGIBLE_PATHS.some((r) => r.test(window.location.pathname));
  }, []);

  useEffect(() => {
    // Guards: SSR, dismissed, converted, non-eligible path, mobile
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem(DISMISSED_KEY)) return;
      if (localStorage.getItem(CONVERTED_KEY)) return;
    } catch { /* ignore */ }
    if (!isEligiblePath()) return;
    // Only desktop (mobile lacks mouseleave-to-top)
    if (window.matchMedia("(max-width: 768px)").matches) return;

    const armTimer = setTimeout(() => setArmed(true), SHOW_DELAY_MS);
    return () => clearTimeout(armTimer);
  }, [isEligiblePath]);

  useEffect(() => {
    if (!armed) return;

    const onMouseLeave = (e) => {
      // Fire when cursor leaves through the top of viewport
      if (e.clientY <= 0 && !open && status !== "done") {
        setOpen(true);
      }
    };
    document.addEventListener("mouseleave", onMouseLeave);
    return () => document.removeEventListener("mouseleave", onMouseLeave);
  }, [armed, open, status]);

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(DISMISSED_KEY, "1"); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const attribution = getUtmAttribution();
      const { data } = await axios.post(`${API}/resources/download`, {
        resource_slug: "hvac-compliance-checklist-2026",
        first_name: form.first_name,
        last_name: "—",
        email: form.email,
        company: form.company,
        source: "exit_intent",
        website: form.website,
        ...attribution,
      });
      try { localStorage.setItem(CONVERTED_KEY, "1"); } catch { /* ignore */ }
      setStatus("done");
      // Auto-open the file
      if (data?.download_url) {
        window.open(data.download_url, "_blank", "noopener,noreferrer");
      }
    } catch (e2) {
      setStatus("error");
      setError(e2?.response?.data?.detail || e2?.message || "Something went wrong");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center px-4"
      role="dialog"
      aria-label="Free HVAC Compliance Checklist"
      data-testid="exit-intent-popup"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div className="bg-white rounded-md w-full max-w-lg shadow-2xl relative overflow-hidden">
        <button
          onClick={dismiss}
          aria-label="Close"
          data-testid="exit-intent-close"
          className="absolute top-4 right-4 p-2 hover:bg-black/5 rounded z-10"
        >
          <X className="h-5 w-5" />
        </button>

        {status !== "done" && (
          <>
            <div className="p-6 md:p-8 bg-gradient-to-br from-brand to-accent text-white">
              <div className="font-mono text-[10px] tracking-overline uppercase text-white/80 mb-3">
                [ Free · No signup · One page ]
              </div>
              <div className="font-display font-bold text-[1.5rem] md:text-[1.75rem] leading-tight tracking-tightest mb-3">
                Before you go &mdash; grab our free HVAC Compliance Checklist.
              </div>
              <div className="text-[14px] text-white/85 leading-relaxed">
                Every statutory item a UK FM should have signed off &mdash; F-Gas, TR/19, BS 9999, TM44, PSSR &mdash;
                with the deadline attached to each.
              </div>
            </div>

            <form onSubmit={submit} className="p-6 space-y-3" data-testid="exit-intent-form">
              <div className="grid grid-cols-2 gap-3">
                <input
                  required
                  placeholder="First name"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="px-3 py-2.5 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand"
                  data-testid="exit-intent-first"
                />
                <input
                  placeholder="Company (optional)"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="px-3 py-2.5 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand"
                  data-testid="exit-intent-company"
                />
              </div>
              <input
                required
                type="email"
                placeholder="Work email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2.5 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand"
                data-testid="exit-intent-email"
              />
              {/* Honeypot */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                className="hidden"
                aria-hidden="true"
              />
              {error && <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</div>}
              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="exit-intent-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-ink hover:bg-brand text-white text-[13px] font-semibold transition disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                {status === "sending" ? "Preparing your download…" : "Send me the checklist"}
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="w-full text-[12px] text-muted-foreground hover:text-ink pt-1"
                data-testid="exit-intent-nothanks"
              >
                No thanks
              </button>
            </form>
          </>
        )}

        {status === "done" && (
          <div className="p-8 text-center" data-testid="exit-intent-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-brand/10 flex items-center justify-center text-brand mb-4">
              <Check className="h-7 w-7" strokeWidth={2.5} />
            </div>
            <div className="font-display font-bold text-[1.35rem] mb-2">Your checklist is on the way</div>
            <p className="text-[13px] text-muted-foreground mb-6">
              A copy has just landed in your inbox. We&apos;ll follow up in a few days with a real-world case
              study you can compare it against.
            </p>
            <button
              onClick={dismiss}
              className="text-[13px] font-semibold text-ink/60 hover:text-ink border-b border-ink/20 pb-0.5"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExitIntentPopup;
